import { ApiError, env, fetchWithAuth, type CountryLite, type CurrencyLite } from '@botto/core';

async function fetchBillingJson<T>(path: string, query: Record<string, string>): Promise<T> {
  const base = env.tariffsApiUrl.replace(/\/$/, '');
  const url = new URL(
    `${base}${path.startsWith('/') ? path : `/${path}`}`,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  for (const [k, v] of Object.entries(query)) {
    url.searchParams.set(k, v);
  }
  const href = base.startsWith('http') ? url.href : `${url.pathname}${url.search}`;
  const res = await fetchWithAuth(href, { method: 'GET' }, 'tariffs');
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
  }
  return (await res.json()) as T;
}

/** GET /countries/ — for cross-module country ISO pickers. */
export async function fetchCountries(): Promise<CountryLite[]> {
  const data = await fetchBillingJson<{ items?: Array<{ country_iso?: string | null; country?: string | null }> }>(
    '/countries/',
    {
      offset: '0',
      limit: '1000',
      'sort.name': 'country',
      'sort.direction': 'asc',
    },
  );
  return (data.items ?? [])
    .filter((c) => c.country_iso != null && c.country_iso !== '')
    .map((c) => ({
      // Keep API casing — tariff prices store country_iso as returned by billing.
      country_iso: String(c.country_iso).trim(),
      country: c.country ?? null,
    }));
}

/** GET /currencies/ — for cross-module currency pickers. */
export async function fetchCurrencies(): Promise<CurrencyLite[]> {
  const data = await fetchBillingJson<{ items?: Array<{ short_name?: string | null; name?: string | null }> }>(
    '/currencies/',
    {
      offset: '0',
      limit: '500',
      'sort.name': 'short_name',
      'sort.direction': 'asc',
    },
  );
  return (data.items ?? [])
    .filter((c) => c.short_name != null && c.short_name !== '')
    .map((c) => ({
      short_name: String(c.short_name),
      name: c.name ?? null,
    }));
}
